import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Check,
  Crown,
  Star,
  MessageCircle,
  Eye,
  Shield,
  Zap,
  Feather,
} from "lucide-react";
import { Helmet } from "react-helmet";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "./ui/alert-dialog";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentPage } from "../redux/navigationSlice.js";
import {
  postWithTokenAPI,
  getWithTokenAPI,
  getWithoutTokenAPI,
} from "../util/Api";
import toast, { Toaster } from "react-hot-toast";
import { Footer } from "./Footer";
import { Header } from "./Header";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function SubscriptionPage({ onNavigate }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const data = useSelector((state) => state.user);
  const token = data?.userId?.accessToken;
  const id = data?.userId?.user?._id;
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [plandetails, setPlanDetails] = useState([]);
  const [subpack, setSubsPack] = useState("");
  const [profileData, setprofileData] = useState("");
  const [userSubscription, setUserSubscription] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [header, setHeader] = useState(null);
  const [profileNumber, setprofileNumber] = useState(null);
  const navigate = useNavigate;
  const [PaymentFile, setPayment] = useState(false);

  // ✅ Fetch user profile + current subscription
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getWithTokenAPI(
          token,
          `/user-id/get_by_id?user_id=${id}`,
        );
        const profile = response?.data?.profile;
        const subpack = response?.data?.subscription;
        if (profile) setprofileData(profile.name);
        setprofileNumber(response?.data?.profile.mobile_number);
        if (subpack) {
          setSubsPack(subpack);
          setUserSubscription({
            plan_id: subpack.plan_id,
            plan_price: subpack.price,
            plan_name: subpack.plan_name,
            status: subpack.status,
            amount: subpack.price,
            start_date: subpack.start_date,
            end_date: subpack.end_date,
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    if (id && token) fetchProfile();
  }, [id, token]);

  // ✅ Fetch subscription plans
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        setIsLoadingPlans(true);
        const response = await getWithoutTokenAPI(`/subscriptionPlan/get_all`);
        if (response?.statusCode === 200) {
          setPlanDetails(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching plan Details:", error);
        toast.error(`${t("Failed to load subscription plans")}`);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlan();
  }, []);

  // ✅ Disable plans cheaper than current
  const isPlanDisabled = (plan) => {
    if (!userSubscription) return false;
    if (userSubscription.plan_name === plan.plan_name) return true;
    if (userSubscription.plan_price >= plan.price) return true;
    return false;
  };

  // ✅ Button text logic
  const getButtonText = (plan) => {
    if (userSubscription?.plan_name === plan.plan_name) return "Current Plan";
    if (userSubscription?.plan_price > plan.price) return plan.plan_name;
    if (plan.price > userSubscription?.plan_price)
      return `Upgrade to ${plan.plan_name}`;
    return `Choose ${plan.plan_name}`;
  };

  // ✅ Date formatter
  const formatDateWithOrdinal = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();
    const suffix = (n) =>
      n > 3 && n < 21 ? "th" : { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th";
    return `${day}${suffix(day)} ${month} ${year}`;
  };

  const handleSubscribe = async (plan) => {
    if (PaymentFile) {
      toast.error(
        `${t("You are already subscribed to this plan or a higher one.")}`,
      );
      return;
    }

    // ✅ 2. Check if the plan is disabled
    if (isPlanDisabled(plan)) {
      toast.error(
        `${t("You are already subscribed to this plan or a higher one.")}`,
      );
      return;
    }

    // 3. Start payment process
    setPayment(true);
    setIsProcessing(plan._id);

    try {
      const amountInPaise = plan.price * 100;
      const orderRes = await postWithTokenAPI(token, "/payment/create-order", {
        plan_id: plan._id,
        user_id: id,
        amount: amountInPaise,
      });

      const order = orderRes?.data?.order;
      if (!order) {
        setPayment(false);
        return toast.error(`${t("Order creation failed.")}`);
      }

      const options = {

   key: "rzp_test_SP5PTA1x9Hrhyx",
        amount: order.amount,
        currency: order.currency,
        name: "Aishwarya matrimony Matrimony",
        description: `${plan.plan_name} Subscription`,
        image: header?.logo,
        order_id: order.id,
        prefill: {
          name: data?.userId?.user?.name || "User",
          email: data?.userId?.user?.email || "user@example.com",
          contact: profileNumber || "9999999999",
        },
        theme: { color: "#e11d48" },
        handler: async (response) => {
          try {
            const verify = await postWithTokenAPI(token, "/payment/verify", {
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature,
            });

            if (verify?.statusCode === 200) {
              setPayment(false);
              toast.success(`${t("🎉 Subscription successful!")}`);

              const updated = await getWithTokenAPI(
                token,
                `/user-id/get_by_id?user_id=${id}`,
              );
              const newSub = updated?.data?.subscription;
              setUserSubscription({
                plan_id: newSub._id,
                plan_price: newSub.plan_price,
                plan_name: newSub.plan_name,
                status: newSub.status,
                amount: newSub.plan_price,
                start_date: newSub.start_date,
                end_date: newSub.end_date,
              });
            } else {
              setPayment(false);
              toast.error(`${t("Payment failed.")}`);
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            setPayment(false);
            toast.error(`${t("Payment failed.")}`);
          }
        },
        modal: {
          ondismiss: () => {
            setPayment(false);
            toast.error(`${t("Payment cancelled by user.")}`);
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", async (response) => {
        setPayment(false);
        toast.error(response.error.description || `${t("Payment failed.")}`);
        try {
          await postWithTokenAPI(token, "/payment/verify", {
            payment_id: response.error.metadata.payment_id,
            order_id: response.error.metadata.order_id,
            signature: response.error.metadata.payment_id,
            status: "failed",
          });
        } catch (err) {
          console.error("Failed payment verification:", err);
        }
      });

      rzp.open();
    } catch (error) {
      setPayment(false);
      console.error(error);
      toast.error(`${t("Something went wrong.")}`);
    } finally {
      setIsProcessing(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />
      <Helmet>
        <title>Aishwarya matrimony - Subscription</title>
        <meta name="description" content="This is my page description." />
        <meta name="keywords" content="React, Helmet, SEO" />
      </Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 mt-3 mb-1">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">
            {t("Find Your Perfect Tamil Life Partner")}
          </h1>
          <p className="text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
            {t(
              "Join thousands of Tamil families who found their happiness through our premium matrimony services. Special features for Tamil community with regional preferences.",
            )}
          </p>
          <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>100% {t("Verified Profiles")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              <span>{t("Tamil Community Focus")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>{t("Fast Matching Algorithm")}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
        {userSubscription && (
          <Card className="mb-8 bg-yellow-50 border-yellow-200">
            <CardContent className="p-6 flex items-center gap-4">
              {userSubscription.plan_name == "Elite" ? (
                <Crown className="h-8 w-8 text-[#001F3F]" />
              ) : userSubscription.plan_name == "Premium" ? (
                <Star className="h-8 w-8 text-blue-600" />
              ) : (
                <Feather className="h-8 w-8 text-green-600" />
              )}
              <div>
                <h3
                  className={`${userSubscription.plan_name === "Premium" ? "text-blue-500" : userSubscription.plan_name === "Elite" ? "text-[#001F3F]" : "text-green-500"} text-xl font-bold`}
                >
                  {t("Current Plan:")} {userSubscription.plan_name}
                </h3>
                <p>
                  {t("Subscription Ends On")}{" "}
                  {formatDateWithOrdinal(userSubscription.end_date)}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plandetails.map((plan) => (
            <Card
              key={plan._id}
              className={`relative ${plan.plan_name === "Free"
                ? "border-gray-500"
                : plan.plan_name === "Premium"
                  ? "border-blue-500"
                  : plan.plan_name === "Elite"
                    ? ""
                    : "border-red-500"
                } ${isPlanDisabled(plan) ? "opacity-50" : ""}`}
              style={plan.plan_name === "Elite" ? { border: "2px solid #001F3F" } : {}}
            >
              <CardHeader className="text-center pb-4">
                <CardTitle
                  className={`text-4xl font-bold ${plan.plan_name === "Free"
                    ? "text-gray-600"
                    : plan.plan_name === "Premium"
                      ? "text-blue-600"
                      : plan.plan_name === "Elite"
                        ? "text-[#001F3F]"
                        : "text-red-600"
                    }`}
                >
                  {plan.plan_name}
                </CardTitle>
                <div className="mt-4">
                  <span className="text-3xl font-bold">
                    ₹
                    {(plan.price / 1).toLocaleString("en-IN", {
                      minimumFractionDigits: 0,
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3 ">
                  {plan?.daily_views_limit > 1 && (
                    <li className="flex justify-start align-center">
                      <Check className="w-4 h-4 text-green-500 mr-1" />
                      {plan?.daily_views_limit} Profiles Viewable
                    </li>
                  )}
                  {plan?.daily_messages_limit > 1 && (
                    <li className="flex justify-start align-center">
                      <Check className="w-4 h-4 text-green-500 mr-1" />
                      {plan?.daily_messages_limit} Messages Limit
                    </li>
                  )}
                  {/* {plan?.features[2] && <li className="flex justify-start align-center"><Check className="w-4 h-4 text-green-500 mr-1"/>{plan?.features[2]}</li>} */}
                  {plan.features?.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full mt-6 cursor-pointer ${plan.plan_name === "Free"
                    ? "bg-gray-600 hover:bg-gray-700"
                    : plan.plan_name === "Premium"
                      ? "bg-blue-600 hover:bg-blue-700"
                      : plan.plan_name === "Elite"
                        ? "text-white"
                        : ""
                    } text-white`}
                  style={plan.plan_name === "Elite" ? { background: 'linear-gradient(to right bottom, #001F3F, #003366)' } : plan.plan_name !== "Free" && plan.plan_name !== "Premium" && plan.plan_name !== "Elite" ? { background: 'linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))' } : {}}
                  onClick={
                    token
                      ? isPlanDisabled(plan)
                        ? undefined
                        : () => handleSubscribe(plan)
                      : () => setShowLoginDialog(true)
                  }
                  disabled={isPlanDisabled(plan) || isProcessing === plan._id}
                >
                  {isProcessing === plan._id
                    ? "Processing..."
                    : getButtonText(plan)}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <AlertDialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Login Required")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("Please log in to continue.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="text-white cursor-pointer"
              style={{ background: 'linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))' }}
              onClick={() => {
                setShowLoginDialog(false);
                navigate("/login");
                // dispatch(setCurrentPage("login"));
              }}
            >
              {t("Login")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}
